import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import {
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form1099OIDInstructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="IRS Form 1099-OID instructions for Tax Year 2025"
          description="Get step-by-step IRS Form 1099-OID instructions for Tax Year 2025. TaxZerone helps you accurately report original issue discount income without errors."
          keywords=""
          canonical="resources/information-returns/form-1099-oid-instructions/"
          ogType="article"
          jsonLdPricing="2.49"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="57448"
        />
      }
    >
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 1099-OID</span>
          </span>{' '}
          Instructions
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/information-returns/form-1099-oid-instructions/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-07T09:00:00-05:00"
                      >
                        July 7, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-07T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 7, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Alexia Zepeda
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 1099-OID at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemProp="articleBody"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Form 1099-OID{' '}
                        </strong>{' '}
                        is used to report original issue discount (OID) income
                        earned on debt instruments such as bonds, notes,
                        certificates of deposit, and other obligations issued at
                        a discount.
                      </span>
                    </li>

                    <li>
                      <span>
                        Recipient copies must be furnished by{' '}
                        <strong style={{ fontWeight: 600 }}> January 31</strong>
                        , and the form must be filed with the IRS by{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          February 28
                        </strong>{' '}
                        if filing on paper or{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          March 31{' '}
                        </strong>{' '}
                        if filing electronically.{' '}
                      </span>
                    </li>

                    <li>
                      <span>
                        {' '}
                        This article covers line-by-line instructions including
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          payer and recipient information, Account Number, 2nd
                          TIN Notice, FATCA Filing Requirement Checkbox, Box 1
                        </strong>{' '}
                        (Original Issue Discount),{' '}
                        <strong style={{ fontWeight: 600 }}>Box 2</strong>{' '}
                        (Other Periodic Interest),{' '}
                        <strong style={{ fontWeight: 600 }}>Box 3</strong>{' '}
                        (Early Withdrawal Penalty),{' '}
                        <strong style={{ fontWeight: 600 }}>Box 4</strong>{' '}
                        (Federal Income Tax Withheld),
                        <strong style={{ fontWeight: 600 }}> Box 5</strong>{' '}
                        (Market Discount),
                        <strong style={{ fontWeight: 600 }}> Box 6</strong>{' '}
                        (Acquisition Premium),{' '}
                        <strong style={{ fontWeight: 600 }}>Box 7</strong>{' '}
                        (Description),{' '}
                        <strong style={{ fontWeight: 600 }}>Box 8</strong>{' '}
                        (Original Issue Discount on U.S. Treasury Obligations),{' '}
                        <strong style={{ fontWeight: 600 }}>Box 9</strong>{' '}
                        (Investment Expenses),{' '}
                        <strong style={{ fontWeight: 600 }}>Box 10</strong>
                        (Bond Premium),{' '}
                        <strong style={{ fontWeight: 600 }}>Box 11</strong>{' '}
                        (Tax-Exempt OID), and{' '}
                        <strong style={{ fontWeight: 600 }}>Boxes 12-14</strong>{' '}
                        (State Information).
                      </span>
                    </li>

                    <li>
                      <span>
                        Late or incorrect filing may result in IRS penalties
                        ranging from
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          $60 to $680 per return
                        </strong>
                        , depending on how late the form is filed or whether
                        there is intentional disregard.
                      </span>
                    </li>

                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Extensions:
                        </strong>{' '}
                        <Link
                          href="/tax-extension/efile-irs-form-8809-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          File Form 8809
                        </Link>{' '}
                        to request an automatic filing extension.{' '}
                        <Link
                          href="/tax-extension/file-irs-form-15397-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          File Form 15397
                        </Link>{' '}
                        to request the full{' '}
                        <strong style={{ fontWeight: 600 }}>
                          30-day extension
                        </strong>{' '}
                        for furnishing recipient copies.
                      </span>
                    </li>
                  </Box>

                  {/* Publisher needs an Organization with a logo ImageObject — no meta tags, same pattern */}
                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>
                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What is IRS Form 1099-OID?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Form 1099-OID, or Original Issue Discount, is used to report
                  the taxable income generated from the difference between the
                  discounted purchase price and the face value of a debt
                  instrument, such as bonds or notes
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Form 1099-OID, or Original Issue Discount, is used to report
                  the taxable income generated from the difference between the
                  discounted purchase price and the face value of a debt
                  instrument, such as bonds or notes, which accrues to the
                  investor over the life of the instrument—even if the interest
                  is not paid out until maturity.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Who needs to file Form 1099-OID?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you are a payer or issuer of these financial instruments,
                  you are required to{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-oid/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    file Form 1099-OID
                  </Link>{' '}
                  to inform both the IRS and the recipient about the original
                  issue discount income earned.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Accurate filing ensures compliance with IRS regulations and
                  helps avoid potential penalties.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  When is the Form 1099-OID due date?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Payers must be aware of two important deadlines related to
                  Form 1099-OID:
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Filing deadline with the IRS
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The deadline for payers to submit Form 1099-OID to the IRS
                  depends on the filing method:
                </Typography>
                <ul style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    <strong>Electronic filing deadline: March 31, 2026</strong>
                    <br />
                    If you are e-filing Form 1099-OID with the IRS, the deadline
                    is March 31st of the year following the tax year for which
                    the form is being issued.
                  </li>

                  <Box
                    component="div"
                    sx={{ mb: 2, p: 2, borderRadius: 2 }}
                    className="gradient-bg"
                  >
                    <Stack
                      direction={{ xs: 'column', md: 'row' }}
                      spacing={2}
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box sx={{ width: '100%' }}>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 600,
                            mb: 1,
                          }}
                        >
                          Avoid the stress of last-minute 1099-OID deadlines.
                        </Typography>
                        <Typography variant="body2">
                          E-file with TaxZerone now and enjoy hassle-free IRS
                          compliance and peace of mind.
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          width: '100%',
                          maxWidth: 150,
                          textAlign: 'center',
                        }}
                      >
                        <Button
                          variant="contained"
                          sx={{
                            textTransform: 'none',
                            boxShadow: 'none',
                            borderRadius: 2,
                            backgroundColor: '#fff',
                            color: '#333',
                            '&:hover, &:focus, &:active': { color: '#fff' },
                          }}
                          size="large"
                          href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1099oidinstructions`}
                        >
                          E-File Now
                        </Button>
                      </Box>
                    </Stack>
                  </Box>
                  <li style={{ marginBottom: 16 }}>
                    <strong>Paper filing deadline: February 28, 2026*</strong>
                    <br /> If you are filing Form 1099-OID with the IRS using
                    traditional paper methods, the deadline is February 28th of
                    the year following the tax year for which the form is
                    issued.
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Recipient copies deadline: January 31, 2026*
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Payers must deliver copies of Form 1099-OID to recipients by
                  January 31st of the year following the relevant tax year. This
                  allows recipients to accurately file their tax returns on
                  time.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> * if the due date falls on the weekend or
                  federal holiday, it is extended to next business days.
                </Alert>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Missing these deadlines or providing incorrect information on
                  Form 1099-OID can lead to IRS penalties. To avoid potential
                  penalties, payers should take steps to ensure that Form
                  1099-OID is filed and distributed both accurately and on time.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Form 1099-OID penalty
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you miss{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-oid/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    filing Form 1099-OID
                  </Link>{' '}
                  within the deadline, the IRS will impose a penalty. Here are
                  the Form 1099-OID penalty rates:
                </Typography>
                <Box component="div" sx={{ mb: 2 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Days late
                          </TableCell>

                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Penalty per return
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>Up to 30 days</TableCell>
                          <TableCell>$60</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>31 days late through August 1</TableCell>
                          <TableCell>$130</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>After August 1 or not filed</TableCell>
                          <TableCell>$340</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Intentional disregard</TableCell>
                          <TableCell>$680</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Stack
                  direction={{ md: 'row', sm: 'column' }}
                  spacing={2}
                  alignItems="center"
                  sx={{
                    mb: 5,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    backgroundSize: {
                      xs: '20% auto',
                      sm: '20% auto',
                      md: '20% auto',
                    },
                    border: '1px solid #ccc',
                  }}
                >
                  <Box sx={{ width: '100%', mb: { md: 0, sm: 1, xs: 2 } }}>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        mb: 0.5,
                        fontSize: '20px !important',
                      }}
                    >
                      Why risk penalties?
                    </Typography>
                    <Typography variant="body2">
                      E-file your 1099-OID forms ahead of the deadline with
                      TaxZerone.
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: '100%',
                      maxWidth: 200,
                      textAlign: 'left',
                    }}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{
                        textTransform: 'none',
                        boxShadow: 'none',
                        borderRadius: 2,
                      }}
                      size="large"
                      href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1099oidinstructions`}
                    >
                      E-file Form 1099-OID
                    </Button>
                  </Box>
                </Stack>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Form 1099-OID Instructions - How to fill out?
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Let&apos;s see line-by-line instructions on how to fill out
                  Form 1099-OID.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Payer details
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-oid-payer-details.webp" />
                  <source srcSet="/assets/images/form-1099-oid-payer-details.png" />
                  <img
                    src="/assets/images/form-1099-oid-payer-details.png"
                    loading="lazy"
                    alt="Payer details"
                    width={525}
                    height={310}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter your name or business name, complete address, and TIN
                  (SSN if you&apos;re an individual; EIN if you&apos;re a
                  business)
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    mt: 2,
                    fontWeight: 600,
                  }}
                >
                  Recipient details
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-oid-recipient-details.webp" />
                  <source srcSet="/assets/images/form-1099-oid-recipient-details.png" />
                  <img
                    src="/assets/images/form-1099-oid-recipient-details.png"
                    loading="lazy"
                    alt="Recipient details"
                    width={525}
                    height={490}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mt: 1, mb: 1 }}>
                  Enter the recipient’s name, complete address, and TIN (SSN for
                  an individual; EIN for a business).
                </Typography>
                <ul style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 12 }}>
                    <b>
                      <i>Account number:</i>
                    </b>{' '}
                    You must provide the account number if you have multiple
                    accounts for the same recipient and filing more than one
                    Form 1099-OID for that recipient.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    <b>
                      <i> 2nd TIN not:</i>
                    </b>{' '}
                    If the IRS has notified you two times within the last 3
                    years that the payee provided an incorrect TIN, you can
                    enter an &quot;X&quot; in this box. By doing so, the IRS
                    will stop sending you further notices about this
                    account&apos;s TIN.
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  FATCA Filing Requirement Checkbox
                </Typography>
                <Typography variant="body1" sx={{ mt: 1, mb: 1 }}>
                  Check this box if you are
                </Typography>
                <ul style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 12 }}>
                    a U.S. payer that is reporting on Form(s) 1099 as part of
                    satisfying your requirement to report with respect to a U.S.
                    account for chapter 4 purposes, as described in Regulations
                    section 1.1471-4(d)(2)(iii)(A).
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    a foreign financial institution (FFI) reporting payments to
                    a U.S. account pursuant to an election described in
                    Regulations section 1.1471-4(d)(5)(i)(A).
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 1: Original Issue Discount
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-oid-original-issue.webp" />
                  <source srcSet="/assets/images/form-1099-oid-original-issue.png" />
                  <img
                    src="/assets/images/form-1099-oid-original-issue.png"
                    loading="lazy"
                    alt="Box 1: Original Issue Discount "
                    width={217}
                    height={80}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Report the taxable OID on the obligation for the part of the
                  year it was owned by the record holder. Do not include the
                  amount reported in box 8.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 2: Other Periodic Interest
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-oid-other-periodic.webp" />
                  <source srcSet="/assets/images/form-1099-oid-other-periodic.png" />
                  <img
                    src="/assets/images/form-1099-oid-other-periodic.png"
                    loading="lazy"
                    alt="Box 2: Other Periodic Interest "
                    width={217}
                    height={79}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter any qualified stated interest paid or credited on this
                  debt obligation during the year. You have two reporting
                  options:
                </Typography>
                <ol style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 12 }}>
                    Report on Form 1099-OID: Enter the amount in Box 2 of this
                    form.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Report on{' '}
                    <Link
                      href="/resources/information-returns/form-1099-int-instructions/"
                      sx={{ color: '#0202ff' }}
                    >
                      Form 1099-INT
                    </Link>{' '}
                    : You may choose to report qualified stated interest on Form
                    1099-INT instead of Form 1099-OID.
                  </li>
                </ol>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the taxable portion of the distribution in Box 2a.
                  However, if you cannot reasonably obtain the data to calculate
                  the taxable amount, leave this box blank.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 3: Early Withdrawal Penalty
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-oid-early-withdrawal.webp" />
                  <source srcSet="/assets/images/form-1099-oid-early-withdrawal.png" />
                  <img
                    src="/assets/images/form-1099-oid-early-withdrawal.png"
                    loading="lazy"
                    alt="Box 3: Early Withdrawal Penalty"
                    width={217}
                    height={54}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the amount of interest or principal forfeited due to
                  early withdrawal of time deposits. This includes:
                </Typography>
                <ul style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 12 }}>
                    Penalties for early withdrawal from Certificates of Deposit
                    (CDs)
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Other time deposit forfeitures that are deductible from the
                    recipient&apos;s gross income
                  </li>{' '}
                </ul>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b>{' '}
                  <Box component="div">
                    <ol>
                      <li style={{ marginBottom: 12 }}>
                        Do not reduce the amounts in Box 1 (Original Issue
                        Discount) or Box 2 (Other Periodic Interest) by the
                        forfeiture amount.
                      </li>
                      <li style={{ marginBottom: 12 }}>
                        The recipient may be able to deduct this penalty from
                        their gross income when filing taxes.
                      </li>
                    </ol>
                  </Box>
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,

                    fontWeight: 600,
                  }}
                >
                  Box 4: Federal Income Tax Withheld
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-oid-federal-income.webp" />
                  <source srcSet="/assets/images/form-1099-oid-federal-income.png" />
                  <img
                    src="/assets/images/form-1099-oid-federal-income.png"
                    loading="lazy"
                    alt="Box 4: Federal income tax withheld"
                    width={217}
                    height={54}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter backup withholding if you have withheld any.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 5: Market Discount
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-oid-market-discount.webp" />
                  <source srcSet="/assets/images/form-1099-oid-market-discount.png" />
                  <img
                    src="/assets/images/form-1099-oid-market-discount.png"
                    loading="lazy"
                    alt="Box 5: Market Discount"
                    width={217}
                    height={79}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Use this box only if all of the following conditions are met:
                </Typography>
                <ol style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 12 }}>The security is covered.</li>

                  <li style={{ marginBottom: 12 }}>
                    The security was acquired with both market discount and
                    Original Issue Discount (OID).
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    The recipient has notified you that they made a section
                    1278(b) election.
                  </li>
                </ol>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  If these conditions are met:
                </Typography>
                <ul style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 12 }}>
                    Enter the amount of market discount that accrued on the debt
                    instrument during the tax year.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Only report amounts of $10 or more.
                  </li>{' '}
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 6: Acquisition Premium
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-oid-acquisition-premium.webp" />
                  <source srcSet="/assets/images/form-1099-oid-acquisition-premium.png" />
                  <img
                    src="/assets/images/form-1099-oid-acquisition-premium.png"
                    loading="lazy"
                    alt="Box 6: Acquisition Premium"
                    width={217}
                    height={79}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you have a covered security purchased with an acquisition
                  premium, enter the amount of premium amortization for the
                  portion of the year that holder owned the debt instrument.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> If you reported a net amount of OID in boxes 1,
                  8, or 11, leave this box blank.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 7: Description
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-oid-description.webp" />
                  <source srcSet="/assets/images/form-1099-oid-description.png" />
                  <img
                    src="/assets/images/form-1099-oid-description.png"
                    loading="lazy"
                    alt="Box 7: Description"
                    width={432}
                    height={131}
                    className="proportional-img"
                  />
                </picture>
                <ul style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 12 }}>
                    Enter the CUSIP number, if available.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    If there is no CUSIP number, provide the stock exchange
                    abbreviation, the issuer abbreviation used by the stock
                    exchange, the coupon rate, and the maturity year (e.g., NYSE
                    XYZ 12 ½ 25).
                  </li>{' '}
                  <li style={{ marginBottom: 12 }}>
                    If the issuer of the obligation differs from the payer, also
                    include the issuer&apos;s name.
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 8: Original Issue Discount on U.S. Treasury Obligations
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-oid-original-discount.webp" />
                  <source srcSet="/assets/images/form-1099-oid-original-discount.png" />
                  <img
                    src="/assets/images/form-1099-oid-original-discount.png"
                    loading="lazy"
                    alt="Box 8: Original Issue Discount on U.S. Treasury Obligations"
                    width={217}
                    height={80}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the amount of Original Issue Discount (OID) on a U.S.
                  Treasury obligation for the part of the year it was owned by
                  the record holder.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  DO NOT include this amount in Box 1.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 9: Investment Expenses
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-oid-investment-expenses.webp" />
                  <source srcSet="/assets/images/form-1099-oid-investment-expenses.png" />
                  <img
                    src="/assets/images/form-1099-oid-investment-expenses.png"
                    loading="lazy"
                    alt="Box 9: Investment Expenses"
                    width={217}
                    height={80}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  For single-class REMICs only. Enter the regular interest
                  holder&apos;s pro rata share of investment expenses. This
                  amount should reflect the expenses incurred in the tax year
                  for which the form is being filed.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 10: Bond Premium
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-oid-bond-premium.webp" />
                  <source srcSet="/assets/images/form-1099-oid-bond-premium.png" />
                  <img
                    src="/assets/images/form-1099-oid-bond-premium.png"
                    loading="lazy"
                    alt="Box 10: Bond Premium"
                    width={217}
                    height={79}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Calculate the bond premium by subtracting the face value of
                  the bond from the purchase price. If you purchased the bond
                  for $1,050 and its face value is $1,000, the bond premium is
                  $50.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the calculated bond premium in Box 10. This represents
                  the excess amount you paid over the face value of the bond.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 11: Tax-Exempt OID
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-oid-tax-exempt.webp" />
                  <source srcSet="/assets/images/form-1099-oid-tax-exempt.png" />
                  <img
                    src="/assets/images/form-1099-oid-tax-exempt.png"
                    loading="lazy"
                    alt="Box 11: Tax-Exempt OID"
                    width={217}
                    height={79}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Determine the amount of OID accrued on the tax-exempt bond
                  during the tax year. This is the portion of the discount that
                  is considered earned for the year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the total amount of tax-exempt OID accrued during the
                  year in Box 11. This amount should reflect the OID that is
                  exempt from federal income tax.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 12 to 14: State information
                </Typography>
                <picture>
                  <source srcSet="/assets/images/form-1099-oid-state-information.webp" />
                  <source srcSet="/assets/images/form-1099-oid-state-information.png" />
                  <img
                    src="/assets/images/form-1099-oid-state-information.png"
                    loading="lazy"
                    alt="Box 12 to 14: State information"
                    width={432}
                    height={80}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <b> Box 12:</b> Enter the 2-letter state abbreviation for the
                  state
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <b>Box 13:</b> Enter the state identification number
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <b> Box 14:</b> Report any state income tax withheld from the
                  payment
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to File Form 1099-OID
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  You can{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-oid/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    file Form 1099-OID
                  </Link>
                  , either electronically or by mail.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Electronic Filing (E-filing)
                </Typography>{' '}
                <Typography variant="body1" sx={{ mb: 2 }}>
                  E-filing is the IRS-recommended method due to its efficiency,
                  accuracy, and quick processing notifications.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  With{' '}
                  <Link href="/" sx={{ color: '#0202ff' }}>
                    {' '}
                    TaxZerone
                  </Link>
                  , you can{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-oid/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    e-file Form 1099-OID
                  </Link>
                  , which{' '}
                  <span style={{ fontWeight: 600 }}>
                    simplifies the entire process
                  </span>{' '}
                  and allows you to complete it{' '}
                  <span style={{ fontWeight: 600 }}>within minutes</span>.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Here&apos;s how to e-file Form 1099-R:
                </Typography>
                <ol style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    Enter the required information, including:
                  </li>
                  <ol style={{ listStyleType: 'circle' }}>
                    <li style={{ marginBottom: 16 }}>
                      Payer&apos;s name, address, and TIN
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      Recipient&apos;s name, address, and TIN
                    </li>
                    <li style={{ marginBottom: 16 }}>Discount details</li>
                  </ol>
                  <li style={{ marginBottom: 16 }}>
                    Review all entered information for accuracy.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Transmit the form securely to the IRS.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Provide a copy to the recipient.
                  </li>
                </ol>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Paper filing
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  While less efficient, paper filing remains an option for those
                  who prefer it. Here are the steps for paper filing:
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Steps for paper filing:
                </Typography>
                <ol style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    Download Form 1099-OID from the official IRS website
                    (www.irs.gov).
                  </li>
                  <li style={{ marginBottom: 16 }}>Print the form.</li>
                  <Alert
                    severity="warning"
                    icon={false}
                    sx={{
                      mb: 3,
                      mt: 0,
                      borderLeft: '3px solid rgb(102, 60, 0)',
                    }}
                  >
                    <b> Note:</b> The IRS requires special red ink forms for
                    submission. You can order these from the IRS or approved
                    vendors.
                  </Alert>
                  <li style={{ marginBottom: 16 }}>
                    Complete all required fields using black ink. This includes:
                  </li>
                  <ol style={{ listStyleType: 'circle' }}>
                    <li style={{ marginBottom: 12 }}>
                      Payer&apos;s information
                    </li>
                    <li style={{ marginBottom: 12 }}>
                      Recipient&apos;s information
                    </li>
                    <li style={{ marginBottom: 12 }}>Discount details</li>
                  </ol>
                  <li style={{ marginBottom: 16 }}>
                    Make a copy for your records.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Mail the original form to the IRS address specified in the
                    Form 1099 General Instructions. The address varies based on
                    your state. Refer to the Where to send Form 1099-OID -
                    Mailing address section to get the address for your state.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Send a copy to the recipient by January 31st of the year
                    following the reportable transaction.
                  </li>
                </ol>
                <Box
                  component="div"
                  sx={{
                    backgroundColor: '#caf0f8',
                    borderLeft: '5px solid #0077b6',
                    p: 1,
                    mb: 4,
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ mb: 1, fontWeight: 600, display: 'flex' }}
                  >
                    <ReportProblemRoundedIcon
                      sx={{ mr: 1, color: 'rgb(237, 108, 2)' }}
                    />{' '}
                    Important considerations for paper filing
                  </Typography>
                  <ul style={{ marginBottom: 6 }}>
                    <li style={{ marginBottom: 16 }}>
                      Mail forms several weeks before the deadline to ensure
                      timely arrival.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      Use certified mail for proof of timely filing.
                    </li>
                    <li style={{ marginBottom: 0 }}>
                      Ensure all copies are legible.
                    </li>
                  </ul>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Alternatively, you can{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-oid/"
                    sx={{ color: '#0202ff' }}
                    id="1099-r-mailing-address"
                  >
                    file Form 1099-OID electronically
                  </Link>{' '}
                  for faster processing and delivery, reducing the risk of
                  delays or lost forms.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Where to send Form 1099-OID - Mailing address
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you prefer paper filing, the mailing address for Form
                  1099-OID varies depending on your business location. Below is
                  a table summarizing the mailing address for Form 1099-OID:
                </Typography>
                <Box component="div" sx={{ mb: 5 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell sx={{ color: '#444', width: 300 }}>
                            If your business operates in or your legal residence
                            is…
                          </TableCell>

                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Mail Form 1099-OID to…
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>
                            Alabama, Arizona, Arkansas, Delaware, Florida,
                            Georgia, Kentucky, Maine, Massachusetts,
                            Mississippi, New Hampshire, New Jersey, New Mexico,
                            New York, North Carolina, Ohio, Texas, Vermont,
                            Virginia
                          </TableCell>
                          <TableCell>
                            Internal Revenue Service
                            <br />
                            Austin Submission Processing Center
                            <br />
                            P.O. Box 149213
                            <br />
                            Austin, TX 78714
                            <br />
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>
                            Alaska, Colorado, Hawaii, Idaho, Illinois, Indiana,
                            Iowa, Kansas, Michigan, Minnesota, Missouri,
                            Montana, Nebraska, Nevada, North Dakota, Oklahoma,
                            Oregon, South Carolina, South Dakota, Tennessee,
                            Utah, Washington, Wisconsin, Wyoming
                          </TableCell>
                          <TableCell>
                            Department of the Treasury
                            <br />
                            IRS Submission Processing Center
                            <br />
                            P.O. Box 219256
                            <br /> Kansas City, MO 64121-9256
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>
                            California, Connecticut, District of Columbia,
                            Louisiana, Maryland, Pennsylvania, Rhode Island,
                            West Virginia
                          </TableCell>
                          <TableCell>
                            Department of the Treasury
                            <br />
                            IRS Submission Processing Center
                            <br />
                            1973 North Rulon White Blvd.
                            <br />
                            Ogden, UT 84201
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Outside the United States</TableCell>
                          <TableCell>
                            Internal Revenue Service,
                            <br /> Austin Submission Processing Center,
                            <br /> P.O. Box 149213, <br />
                            Austin, TX 78714
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to E-File Form 1099-OID with TaxZerone
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Before you start{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-oid/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    e-filing Form 1099-OID
                  </Link>
                  , ensure you have all the necessary information to facilitate
                  a smooth and efficient filing experience. Here’s what you need
                  to gather:
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Required information for filing Form 1099-OID:
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 16 }}>Business details</li>
                  <li style={{ marginBottom: 16 }}>Recipient details</li>
                  <li style={{ marginBottom: 16 }}>
                    Original issue discount for the year
                  </li>
                  <li style={{ marginBottom: 16 }}>Periodic interest</li>
                  <li style={{ marginBottom: 16 }}>Early withdrawal penalty</li>
                  <li style={{ marginBottom: 16 }}>Market discount</li>
                  <li style={{ marginBottom: 16 }}>
                    Federal and state income tax withheld
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Steps to e-file Form 1099-OID using TaxZerone:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>Step 1: Complete Form 1099-OID</b>
                  <br />
                  Open Form 1099-OID and fill out the necessary fields with the
                  information you have gathered.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>Step 2: Review and transmit the return</b>
                  <br />
                  Carefully review all the entered information to ensure
                  accuracy. Once verified, transmit the completed Form 1099-OID
                  to the IRS.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <b>Step 3: Provide the recipient copy</b>
                  <br />
                  After successfully transmitting the form to the IRS, send a
                  copy of the completed Form 1099-OID to the recipient.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Benefits of E-Filing Form 1099-OID with TaxZerone
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  <Link
                    href="/information-returns/efile-irs-form-1099-oid/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    E-filing Form 1099-OID
                  </Link>{' '}
                  with TaxZerone offers several advantages:
                </Typography>
                <List sx={{ mb: 0 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <b> IRS Form Validations:</b> TaxZerone&apos;s system
                      checks your returns for errors or missing information,
                      ensuring accuracy before submission.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <b>Email Recipient Copies:</b> Conveniently send recipient
                      copies of Form 1099-OID via email, eliminating the need
                      for manual mailing.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <b> Secure Portal Access (ZeroneVault):</b> Recipients can
                      access their return copies anytime, anywhere through the
                      secure ZeroneVault portal.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <b>Competitive Pricing:</b> TaxZerone offers
                      industry-leading pricing, with rates starting as low as
                      $0.59 per form, making it affordable for businesses of all
                      sizes.
                    </ListItemText>
                  </ListItem>
                </List>
                <Typography variant="body1">
                  By using TaxZerone&apos;s e-filing solution, you can
                  streamline your Form 1099-OID filing, save time, reduce
                  errors, and provide a better experience for your recipients.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 30, md: 26, sm: 22, xs: 18 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Maximize the efficiency of your tax reporting by{' '}
              <Hidden smDown>
                <br />
              </Hidden>
              choosing TaxZerone for e-filing Form 1099-OID.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1099oidinstructions`}
              >
                E-file Form 1099-OID
              </Button>
              {/* <Typography variant="body2" sx={{ mt: 1 }}>
                Effortless, efficient, and affordable e-filing awaits you.
              </Typography> */}
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form1099OIDInstructions;
